<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAuthStore } from '@/store/auth';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';

const authStore = useAuthStore();
const router = useRouter();
// 반응성을 유지하며 값을 가져옵니다.
const { allowLotto } = storeToRefs(authStore);

const isLoggedIn = computed(() => authStore.isLoggedIn);
const mnickname = computed(() => authStore.mnickname);
const mlevel = computed(() => authStore.mlevel || 1);
const menergy = computed(() => authStore.menergy || 0);

const isScrolled = ref(false);
const isMobileMenuOpen = ref(false);
const activeDropdown = ref(null);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

const closeDropdown = () => {
  activeDropdown.value = null;
};

const handleLogout = () => {
  if (confirm('로그아웃 하시겠습니까?')) {
    authStore.logout();
    router.push('/login');
    closeMobileMenu();
    closeDropdown();
  }
};

const goToEnergyHistory = () => {
  router.push('/energy-history');
  closeDropdown();
  closeMobileMenu();
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
  <header
    class="top-navbar"
    :class="{ scrolled: isScrolled }"
  >
    <div class="nav-container">
      <router-link
        to="/"
        class="logo-area"
        @click="closeMobileMenu"
      >
        <img
          src="logo-192.png"
          alt="말로만랩 Logo"
          class="logo-img"
        >
        <span class="logo-text">말로만랩</span>
      </router-link>

      <nav class="pc-nav">
        <div
          class="nav-item-group group-mining"
          @mouseenter="activeDropdown = 'mining'"
          @mouseleave="activeDropdown = null"
        >
          <span class="nav-label">마닝</span>
          <transition name="dropdown">
            <div
              v-if="activeDropdown === 'mining'"
              class="dropdown-menu grouped-menu"
              @click="closeDropdown"
            >
              <div class="menu-group">
                <span class="group-title">탐험 (Explore)</span>
                <router-link
                  to="/journey"
                  class="dropdown-item item-red"
                >
                  <span class="dot" /> 여정 (발견)
                </router-link>
                <router-link
                  to="/mining"
                  class="dropdown-item item-blue"
                >
                  <span class="dot" /> 누리 (피드)
                </router-link>
                <router-link
                  to="/favorites"
                  class="dropdown-item item-purple"
                >
                  <span class="dot" /> 갈무리 (보관함)
                </router-link>
              </div>
              <div class="menu-divider" />
              <div class="menu-group">
                <span class="group-title">경험 (Experience)</span>
                <router-link
                  to="/quiz"
                  class="dropdown-item item-green"
                >
                  <span class="dot" /> 슬기 (일반 퀴즈)
                </router-link>
                <router-link
                  to="/speedquiz"
                  class="dropdown-item item-orange"
                >
                  <span class="dot" /> 재치 (스피드 퀴즈)
                </router-link>
                <router-link to="/scratch" class="dropdown-item item-pink">
                  <span class="dot" /> 야금 (스크래치 게임)
                </router-link>
                <router-link to="/silmari" class="dropdown-item item-purple">
                  <span class="dot" /> 실마리 (추리하는 지식)
                </router-link>
              </div>
            </div>
          </transition>
        </div>

        <div
          class="nav-item-group group-studio"
          @mouseenter="activeDropdown = 'studio'"
          @mouseleave="activeDropdown = null"
        >
          <span class="nav-label">공방</span>
          <transition name="dropdown">
            <div
              v-if="activeDropdown === 'studio'"
              class="dropdown-menu grouped-menu"
              @click="closeDropdown"
            >
              <div class="menu-group">
                <span class="group-title">상상 (Imagination)</span>
                <router-link to="/mystery" class="dropdown-item item-cyan">
                  <span class="dot" /> 추리사건의 전말
                </router-link>
                <router-link
                  to="/receipt"
                  class="dropdown-item item-indigo"
                >
                  <span class="dot" /> 욕망의 편의점
                </router-link>
                <router-link
                  to="/award"
                  class="dropdown-item item-yellow"
                >
                  <span class="dot" /> 뻔뻔한 위원회
                </router-link>
              </div>

              <div class="menu-divider" />

              <div class="menu-group">
                <span class="group-title">기술 (Technology)</span>
                  <router-link
                    to="/chimpanzee"
                    class="dropdown-item item-teal"
                  >
                    <span class="dot" /> 침팬지보다 낫겠지
                  </router-link>
                  <router-link to="/math-give-up" class="dropdown-item item-orange">
                    <span class="dot" /> 수포자 5초 전
                  </router-link>
                  <router-link to="/easy-math" class="dropdown-item item-lime">
                    <span class="dot lime" /> 아주 쉬운 덧셈, 뺄셈
                  </router-link>
                  <router-link to="/auth-code" class="dropdown-item item-blue">
                    <span class="dot" /> 인증번호를 외우고 싶어
                  </router-link>
                  <router-link to="/physical-exam" class="dropdown-item item-blue">
                    <span class="dot" /> 디지털 신체검사
                  </router-link>
                  <router-link
                    to="/cant-match"
                    class="dropdown-item item-red"
                  >
                    <span class="dot" /> 그걸 못 맞춰?
                  </router-link>
                  <router-link 
                      v-if="allowLotto" 
                      to="/secret-lotto" 
                      class="dropdown-item item-secret"
                      @click="closeDropdown"
                    >
                      <span class="dot neon" /> 🔐 시크릿 로또 분석기
                    </router-link>
              </div>
            </div>
          </transition>
        </div>

        <div
          class="nav-item-group group-support"
          @mouseenter="activeDropdown = 'support'"
          @mouseleave="activeDropdown = null"
        >
          <span class="nav-label">지원</span>
          <transition name="dropdown">
            <div
              v-if="activeDropdown === 'support'"
              class="dropdown-menu simple-menu"
              @click="closeDropdown"
            >
              <router-link
                to="/partnership"
                class="dropdown-item item-pink"
              >
                제휴 문의
              </router-link>
              <router-link
                to="/devinquiry"
                class="dropdown-item item-yellow"
              >
                개발 문의
              </router-link>
              <router-link
                to="/settings"
                class="dropdown-item item-gray"
              >
                설정
              </router-link>
            </div>
          </transition>
        </div>
      </nav>

      <div class="right-actions">
        <router-link
          to="/search"
          class="icon-btn search-btn"
          aria-label="검색"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          ><circle
            cx="11"
            cy="11"
            r="8"
          /><line
            x1="21"
            y1="21"
            x2="16.65"
            y2="16.65"
          /></svg>
        </router-link>

        <template v-if="isLoggedIn">
          <div
            class="user-badge"
            @click="goToEnergyHistory"
          >
            <span class="level-tag">{{ mlevel }}살</span>
            <span class="username">{{ mnickname }}</span>
            <div class="divider" />
            <span class="energy-info">⚡ {{ menergy }}</span>
          </div>
        </template>
        <template v-else>
          <router-link
            to="/login"
            class="login-btn"
          >
            로그인
          </router-link>
        </template>

        <button
          class="mobile-menu-btn"
          @click="toggleMobileMenu"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          ><line
            x1="3"
            y1="12"
            x2="21"
            y2="12"
          /><line
            x1="3"
            y1="6"
            x2="21"
            y2="6"
          /><line
            x1="3"
            y1="18"
            x2="21"
            y2="18"
          /></svg>
        </button>
      </div>
    </div>

    <transition name="slide-fade">
      <div
        v-if="isMobileMenuOpen"
        class="mobile-menu-wrapper"
      >
        <div class="mobile-header">
          <div class="mobile-header-left">
            <span class="mobile-menu-title">전체 메뉴</span>
          </div>
          <button
            class="close-btn"
            @click="closeMobileMenu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            ><line
              x1="18"
              y1="6"
              x2="6"
              y2="18"
            /><line
              x1="6"
              y1="6"
              x2="18"
              y2="18"
            /></svg>
          </button>
        </div>

        <div class="mobile-content-scroll">
          <div
            v-if="isLoggedIn"
            class="mobile-profile-card fade-in-up"
          >
            <div class="profile-row">
              <div class="avatar-circle">
                {{ mnickname.charAt(0) }}
              </div>
              <div class="profile-texts">
                <span class="welcome-text">반가워요!</span>
                <span class="user-name-text">{{ mnickname }}님</span>
              </div>
            </div>
            <div
              class="energy-row"
              @click="goToEnergyHistory"
            >
              <span class="energy-label">⚡ 보유 에너지</span>
              <span class="energy-val">{{ menergy.toLocaleString() }}</span>
            </div>
          </div>

          <div
            v-else
            class="mobile-profile-card guest-mode fade-in-up"
          >
            <p class="guest-msg">
              로그인하고 더 많은<br>지식을 탐험해보세요!
            </p>
            <router-link
              to="/login"
              class="mobile-login-btn"
              @click="closeMobileMenu"
            >
              로그인 / 회원가입
            </router-link>
          </div>

          <div
            class="mobile-section fade-in-up"
            style="animation-delay: 0.1s"
          >
            <h3 class="section-label">
              탐험 (Explore)
            </h3>
            <div class="menu-grid">
              <router-link
                to="/journey"
                class="menu-card"
                @click="closeMobileMenu"
              >
                <div class="icon-circle red">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ><circle
                    cx="12"
                    cy="12"
                    r="10"
                  /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" /></svg>
                </div>
                <span class="menu-text">여정 (발견)</span>
              </router-link>
              <router-link
                to="/mining"
                class="menu-card"
                @click="closeMobileMenu"
              >
                <div class="icon-circle blue">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ><rect
                    x="3"
                    y="3"
                    width="7"
                    height="7"
                  /><rect
                    x="14"
                    y="3"
                    width="7"
                    height="7"
                  /><rect
                    x="14"
                    y="14"
                    width="7"
                    height="7"
                  /><rect
                    x="3"
                    y="14"
                    width="7"
                    height="7"
                  /></svg>
                </div>
                <span class="menu-text">누리 (피드)</span>
              </router-link>
              <router-link
                to="/favorites"
                class="menu-card"
                @click="closeMobileMenu"
              >
                <div class="icon-circle purple">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
                </div>
                <span class="menu-text">갈무리 (보관함)</span>
              </router-link>
            </div>
          </div>

          <div class="mobile-section fade-in-up" style="animation-delay: 0.2s">
            <h3 class="section-label">경험 (Experience)</h3>
            <div class="menu-grid">
              <router-link to="/quiz" class="menu-card" @click="closeMobileMenu">
                <div class="icon-circle green"><svg>...</svg></div>
                <span class="menu-text">슬기 (퀴즈)</span>
              </router-link>
              
              <router-link to="/speedquiz" class="menu-card" @click="closeMobileMenu">
                <div class="icon-circle orange"><svg>...</svg></div>
                <span class="menu-text">재치 (스피드)</span>
              </router-link>

              <router-link to="/scratch" class="menu-card" @click="closeMobileMenu">
                <div class="icon-circle pink">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line>
                  </svg>
                </div>
                <span class="menu-text">야금 (스크래치)</span>
              </router-link>
              <router-link to="/silmari" class="menu-card" @click="closeMobileMenu">
                <div class="icon-circle purple">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </div>
                <span class="menu-text">실마리 (추리)</span>
              </router-link>
            </div>
          </div>

          <div
            class="mobile-section fade-in-up"
            style="animation-delay: 0.3s"
          >
            <h3 class="section-label">
              공방 (Studio)
            </h3>
            
            <div class="sub-label-mobile">상상 (Imagination)</div>
            <div class="menu-list-simple">
              <router-link to="/mystery" class="simple-item" @click="closeMobileMenu">
                <span class="dot cyan" /> 추리사건의 전말
              </router-link>
              <router-link
                to="/receipt"
                class="simple-item"
                @click="closeMobileMenu"
              >
                <span class="dot indigo" /> 욕망의 편의점
              </router-link>
              <router-link
                to="/award"
                class="simple-item"
                @click="closeMobileMenu"
              >
                <span class="dot yellow" /> 뻔뻔한 위원회
              </router-link>
            </div>

            <div class="sub-label-mobile mt-small">기술 (Technology)</div>
              <div class="menu-list-simple">
                <router-link
                  to="/chimpanzee"
                  class="simple-item"
                  @click="closeMobileMenu"
                >
                  <span class="dot teal" /> 침팬지보다 낫겠지
                </router-link>
                <router-link to="/math-give-up" class="simple-item" @click="closeMobileMenu">
                  <span class="dot orange" /> 수포자 5초 전
                </router-link>
                <router-link to="/easy-math" class="simple-item" @click="closeMobileMenu">
                  <span class="dot lime" /> 아주 쉬운 덧셈, 뺄셈
                </router-link>
                <router-link to="/auth-code" class="simple-item" @click="closeMobileMenu">
                  <span class="dot blue" /> 인증번호를 외우고 싶어
                </router-link>
                <router-link to="/physical-exam" class="simple-item" @click="closeMobileMenu">
                  <span class="dot blue" /> 디지털 신체검사
                </router-link>
                <router-link
                  to="/cant-match"
                  class="simple-item"
                  @click="closeMobileMenu"
                >
                  <span class="dot pink" /> 그걸 못 맞춰?
                </router-link>
                <router-link 
                        v-if="allowLotto" 
                        to="/secret-lotto" 
                        class="simple-item item-secret-mobile"
                        @click="closeMobileMenu"
                      >
                        <span class="dot neon" /> 🔐 시크릿 로또
                      </router-link>
              </div>
          </div>

          <div
            class="mobile-section fade-in-up"
            style="animation-delay: 0.4s"
          >
            <h3 class="section-label">
              지원 (Support)
            </h3>
            <div class="menu-list-simple">
              <router-link
                to="/partnership"
                class="simple-item"
                @click="closeMobileMenu"
              >
                <span class="dot pink" /> 제휴 문의
              </router-link>
              <router-link
                to="/devinquiry"
                class="simple-item"
                @click="closeMobileMenu"
              >
                <span class="dot yellow" /> 개발 문의
              </router-link>
              <router-link
                to="/settings"
                class="simple-item"
                @click="closeMobileMenu"
              >
                <span class="dot gray" /> 설정
              </router-link>
            </div>
          </div>

          <div
            v-if="isLoggedIn"
            class="mobile-footer fade-in-up"
            style="animation-delay: 0.5s"
          >
            <button
              class="logout-btn-mobile"
              @click="handleLogout"
            >
              로그아웃
            </button>
            <p class="version-text">
              v1.0.0 Malomanlab
            </p>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<style scoped>
/* PC 네비게이션 스타일은 기존 유지 */
.top-navbar {
  position: fixed; top: 0; left: 0; width: 100%; height: var(--header-height);
  z-index: 1000; background: transparent; transition: background 0.3s, backdrop-filter 0.3s;
  border-bottom: 1px solid transparent;
}
.top-navbar.scrolled {
  background: rgba(15, 17, 26, 0.85);
  backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-color);
}
.nav-container {
  max-width: 1400px; height: 100%; margin: 0 auto; padding: 0 24px;
  display: flex; align-items: center; justify-content: space-between;
}
.logo-area { display: flex; align-items: center; gap: 10px; font-size: 1.3rem; font-weight: 800; text-decoration: none; }
.logo-img { width: 20px; height: 20px; object-fit: contain; }
.logo-text {
  background: linear-gradient(90deg, #fff, #b0b8c4);
  background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent;
}
.pc-nav { display: flex; gap: 40px; height: 100%; }
.nav-item-group { position: relative; height: 100%; display: flex; align-items: center; cursor: pointer; }
.nav-label { font-size: 1rem; font-weight: 600; color: var(--text-sub); transition: color 0.2s; position: relative; }
.nav-item-group:hover .nav-label { color: #fff; }
.nav-item-group:hover .nav-label::after {
  content: ""; position: absolute; bottom: -4px; left: 0; width: 100%; height: 2px;
}

/* Hover colors */
.group-mining:hover .nav-label::after { background: #ff7675; box-shadow: 0 0 8px #ff7675; }
.group-studio:hover .nav-label::after { background: #54a0ff; box-shadow: 0 0 8px #54a0ff; }
.group-support:hover .nav-label::after { background: #a29bfe; box-shadow: 0 0 8px #a29bfe; }

.dropdown-menu {
  position: absolute; top: calc(var(--header-height) - 10px); left: 50%; transform: translateX(-50%);
  background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5); overflow: hidden; display: flex;
}
.grouped-menu { min-width: 340px; flex-direction: column; padding: 12px 0; }
.menu-group { padding: 4px 0; }
.group-title { display: block; font-size: 0.75rem; color: #636e72; font-weight: 700; padding: 4px 20px; margin-bottom: 4px; text-transform: uppercase; }
.menu-divider { height: 1px; background: rgba(255, 255, 255, 0.05); margin: 8px 0; }
.simple-menu { min-width: 160px; padding: 8px 0; flex-direction: column; }
.dropdown-item {
  display: flex; align-items: center; gap: 8px; text-decoration: none;
  padding: 10px 24px; font-size: 0.95rem; color: var(--text-sub); transition: all 0.2s;
}
.dropdown-item:hover { background: rgba(255, 255, 255, 0.05); color: #fff; padding-left: 28px; }

/* [추가] 준비 중 아이템 스타일 */
.empty-item {
  color: #5a6b7c; cursor: default; font-size: 0.9rem; padding: 10px 24px; display: flex; align-items: center; gap: 8px;
}
.empty-item:hover { background: none; color: #5a6b7c; padding-left: 24px; }

/* Dots */
.dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; opacity: 0.7; }
.item-red .dot, .dot.red { background: #ff4d4d; }
.item-blue .dot, .dot.blue { background: #48dbfb; }
.item-purple .dot { background: #a29bfe; }
.item-green .dot { background: #2fd5c6; }
.item-orange .dot, .dot.orange { background: #ff9f43; }
.item-mint .dot, .dot.mint { background: #00b894; }
.item-pink .dot { background: #ff7979; }
.item-yellow .dot { background: #feca57; }
.item-gray .dot { background: #b2bec3; }
.item-indigo .dot { background: #5f27cd; }
.item-brown .dot { background: #cd6133; }
.item-teal .dot, .dot.teal { background: #1abc9c; }
.item-cyan .dot, .dot.cyan { background: #00e5ff; box-shadow: 0 0 8px rgba(0, 229, 255, 0.5); }
.item-lime:hover { background: rgba(50, 255, 126, 0.1) !important; color: #fff; }
.dot.lime { background: #32ff7e !important; box-shadow: 0 0 8px #32ff7e !important; }

/* Right Actions */
.right-actions { display: flex; align-items: center; gap: 20px; }
.icon-btn { display: flex; align-items: center; justify-content: center; color: var(--text-sub); transition: color 0.2s; padding: 8px; }
.icon-btn:hover { color: #fff; }
.login-btn { padding: 8px 20px; border: 1px solid var(--text-sub); border-radius: 24px; font-size: 0.9rem; color: #fff; transition: all 0.2s; text-decoration: none; }
.login-btn:hover { border-color: #fff; background: rgba(255, 255, 255, 0.1); }
.user-badge {
  display: flex; align-items: center; gap: 10px; cursor: pointer;
  background: rgba(255, 255, 255, 0.05); padding: 6px 16px;
  border-radius: 24px; border: 1px solid rgba(255, 255, 255, 0.05); transition: all 0.2s;
}
.user-badge:hover { background: rgba(255, 255, 255, 0.1); border-color: rgba(255, 255, 255, 0.2); }
.level-tag { background: var(--accent-mint); color: #000; font-size: 0.8rem; font-weight: 800; padding: 2px 8px; border-radius: 6px; }
.username { font-weight: 600; color: #fff; font-size: 0.95rem; }
.divider { width: 1px; height: 12px; background: rgba(255, 255, 255, 0.2); }
.energy-info { font-size: 0.9rem; color: var(--accent-orange); font-weight: 600; }

.mobile-menu-btn { display: none; background: none; border: none; color: #fff; cursor: pointer; padding: 4px; }

/* Mobile Menu Styles */
.mobile-menu-wrapper {
  position: fixed; top: 0; left: 0; width: 100%; height: 100vh;
  background: #0f111a; z-index: 1100; display: flex; flex-direction: column;
}

.mobile-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 15px 24px; border-bottom: 1px solid rgba(255,255,255,0.05);
  background: rgba(15, 17, 26, 0.95);
}
.mobile-menu-title { font-size: 1.2rem; font-weight: 800; color: #fff; }
.close-btn { background: none; border: none; color: #b0b8c4; cursor: pointer; padding: 4px; }

.mobile-content-scroll {
  flex: 1; overflow-y: auto; padding: 24px;
}

/* Profile Card */
.mobile-profile-card {
  background: linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
}
.profile-row { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
.avatar-circle {
  width: 48px; height: 48px; border-radius: 50%;
  background: linear-gradient(135deg, #3498db, #8e44ad);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.2rem; font-weight: 800; color: #fff;
  box-shadow: 0 4px 15px rgba(52, 152, 219, 0.4);
}
.profile-texts { display: flex; flex-direction: column; }
.welcome-text { font-size: 0.85rem; color: #b0b8c4; margin-bottom: 2px; }
.user-name-text { font-size: 1.1rem; font-weight: 800; color: #fff; }

.energy-row {
  background: rgba(0,0,0,0.3); border-radius: 12px; padding: 12px 16px;
  display: flex; justify-content: space-between; align-items: center;
  border: 1px solid rgba(255,255,255,0.05); cursor: pointer;
}
.energy-label { font-size: 0.9rem; color: #b0b8c4; font-weight: 600; }
.energy-val { font-size: 1.1rem; font-weight: 800; color: #feca57; }

.guest-mode { text-align: center; }
.guest-msg { color: #b0b8c4; margin-bottom: 16px; line-height: 1.5; }
.mobile-login-btn {
  display: block; width: 100%; padding: 12px;
  background: #1abc9c; color: #fff; font-weight: 700; border-radius: 12px;
  text-decoration: none;
}

/* Section & Grid */
.mobile-section { margin-bottom: 32px; }
.section-label {
  font-size: 0.85rem; color: #636e72; font-weight: 700;
  text-transform: uppercase; margin-bottom: 12px; padding-left: 4px;
}

/* [추가] 모바일 소제목 스타일 */
.sub-label-mobile {
  font-size: 0.75rem; color: #636e72; font-weight: 700;
  margin: 10px 0 6px 4px; 
}
.mt-small { margin-top: 16px; }

.menu-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
}
.menu-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 16px 8px;
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  text-decoration: none; color: #fff;
  transition: all 0.2s;
}
.menu-card:active { transform: scale(0.96); background: rgba(255,255,255,0.08); }

.icon-circle {
  width: 40px; height: 40px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; margin-bottom: 2px;
}
.menu-text { font-size: 0.8rem; font-weight: 600; text-align: center; word-break: keep-all; }

.icon-circle.red { background: rgba(255, 77, 77, 0.15); color: #ff4d4d; }
.icon-circle.blue { background: rgba(72, 219, 251, 0.15); color: #48dbfb; }
.icon-circle.purple { background: rgba(162, 155, 254, 0.15); color: #a29bfe; }
.icon-circle.green { background: rgba(46, 213, 115, 0.15); color: #2ed573; }
.icon-circle.orange { background: rgba(255, 159, 67, 0.15); color: #ff9f43; }
.icon-circle.mint { background: rgba(26, 188, 156, 0.15); color: #1abc9c; }

/* Simple List */
.menu-list-simple {
  background: rgba(255, 255, 255, 0.03); border-radius: 16px; overflow: hidden;
}
.simple-item {
  display: flex; align-items: center; gap: 12px;
  padding: 16px 20px; text-decoration: none; color: #b0b8c4; font-weight: 600; font-size: 0.95rem;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.simple-item:last-child { border-bottom: none; }
.simple-item .dot { width: 8px; height: 8px; border-radius: 50%; }

/* Mobile Dots */
.dot.indigo { background: #5f27cd; }
.dot.pink { background: #ff7979; }
.dot.yellow { background: #feca57; }
.dot.gray { background: #b2bec3; }

/* ⭐️ 시크릿 메뉴 전용 스타일 (해커 느낌) */
.item-secret {
  color: #00ff00 !important; /* 네온 그린 */
  font-weight: 700;
  background: rgba(0, 255, 0, 0.05);
  margin-top: 5px;
  border-top: 1px dashed rgba(0, 255, 0, 0.3);
}
.item-secret:hover {
  background: rgba(0, 255, 0, 0.15);
  text-shadow: 0 0 10px #00ff00;
}
.dot.neon { 
  background: #00ff00; 
  box-shadow: 0 0 8px #00ff00; 
}

/* ⭐️ 모바일용 시크릿 메뉴 스타일 */
.item-secret-mobile {
  color: #00ff00 !important;
  font-weight: 700;
  background: rgba(0, 255, 0, 0.05);
  border-top: 1px dashed rgba(0, 255, 0, 0.2);
  border-bottom: 1px dashed rgba(0, 255, 0, 0.2);
}
.item-secret-mobile:active {
  background: rgba(0, 255, 0, 0.15);
}

/* Logout */
.logout-btn-mobile {
  width: 100%; padding: 16px;
  background: rgba(231, 76, 60, 0.1); border: 1px solid rgba(231, 76, 60, 0.3);
  color: #ff6b6b; border-radius: 16px; font-weight: 700; cursor: pointer;
  margin-bottom: 20px;
}
.version-text { text-align: center; color: #5a6b7c; font-size: 0.8rem; }

@media (max-width: 768px) {
  .pc-nav { display: none; }
  .mobile-menu-btn { display: block; }
  .user-badge { display: none; }
}

/* Animation */
.slide-fade-enter-active, .slide-fade-leave-active { transition: all 0.3s ease; }
.slide-fade-enter-from, .slide-fade-leave-to { transform: translateX(100%); opacity: 0; }
.fade-in-up { animation: fadeInUp 0.5s ease-out backwards; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
</style>